import { type ComponentProps, type RefObject, createContext, useContext, useMemo, useRef } from 'react';

import { Box } from '@betterment/samba-react';
import { DataContext, XYChart as VisxXYChart } from '@visx/xychart';

import { type ThemeConfig, createSambaChartTheme } from './theme';

import type { SystemStyleObject } from '@betterment/samba-styled-system/types';

export type XYChartProps = Omit<ComponentProps<typeof VisxXYChart>, 'aria-label' | 'aria-describedby' | 'theme'> & {
  'aria-label': string;
  css?: SystemStyleObject;
  theme?: Partial<ThemeConfig>;
  'aria-describedby': string;
};

export const XYChartRefContext = createContext<RefObject<HTMLDivElement | null> | null>(null);

export const XYChart = ({
  children,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedby,
  css: cssProp,
  theme,
  ...otherProps
}: XYChartProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const dataContext = useContext(DataContext);

  const chartTheme = useMemo(() => {
    if (dataContext?.theme) {
      return dataContext.theme;
    }
    return createSambaChartTheme(theme);
  }, [dataContext?.theme, theme]);

  return (
    <XYChartRefContext.Provider value={ref}>
      <Box
        ref={ref}
        tabIndex={0}
        aria-label={ariaLabel}
        css={{ display: 'inline-block', '& svg': { overflow: 'visible' }, overflow: 'visible', ...cssProp }}
        aria-describedby={ariaDescribedby}
        role="img"
      >
        <VisxXYChart theme={chartTheme} {...otherProps}>
          {children}
        </VisxXYChart>
      </Box>
    </XYChartRefContext.Provider>
  );
};
